import { ArrowLeft, CopyPlus, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "wouter";
import RequestForm, { type RequestPrefill } from "../components/RequestForm";
import SavedBriefs from "../components/SavedBriefs";
import { HeroSkeleton, useSlowEnoughToShow } from "../components/Skeleton";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import type { Service } from "../types";

/**
 * Requesting a service, as a page.
 *
 * It was a modal. A modal is the wrong container for the longest form in the
 * product: it traps scroll on a phone, the browser's back button closes the whole
 * app instead of the dialogue, a half-written brief is one stray click on the
 * backdrop away from being gone, and the state cannot be linked to — so "start a
 * request for a reel" was a thing you had to describe rather than send.
 *
 * As a route it costs nothing extra and fixes all four. It also gives the copy of a
 * past request somewhere to live: `?from=<id>` is URL state, not another flag.
 */
export default function NewRequestPage({
  services,
  month,
  onCreated,
  onCancel,
}: {
  services: Service[];
  month: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [params] = useSearchParams();
  const serviceId = params.get("service") || undefined;
  const from = params.get("from");
  const templateId = params.get("template");

  const [prefill, setPrefill] = useState<RequestPrefill | undefined>();
  const [loading, setLoading] = useState(Boolean(from));
  const [failed, setFailed] = useState<string | null>(null);
  const showSkeleton = useSlowEnoughToShow(loading);

  useEffect(() => {
    if (!from && !templateId) { setPrefill(undefined); setLoading(false); setFailed(null); return; }
    let live = true;
    setLoading(true);
    setFailed(null);

    // Two sources, one shape. A copied request and a saved brief fill the same
    // fields for the same reason, so the form is told about neither.
    const source = from
      ? dashboardApi.reorder(from)
      : dashboardApi.template(templateId!).then((template) => ({
        serviceId: template.serviceId,
        title: template.title,
        brief: template.brief,
        quantity: template.quantity,
        language: template.language,
        channel: template.channel,
        referenceUrl: template.referenceUrl,
      }));

    source
      .then((values) => { if (live) { setPrefill(values); setLoading(false); } })
      .catch((error: unknown) => {
        // Say what went wrong and leave the empty form usable underneath. A copy
        // that cannot be made is a reason to start a request by hand, not a reason
        // to be sent back to a list.
        if (!live) return;
        setFailed(error instanceof Error ? error.message : t("error.copyRequest"));
        setLoading(false);
      });
    return () => { live = false; };
  }, [from, templateId]);

  return (
    <div className="page-stack new-request-page">
      {/* No heading here. The app shell already titles this route "Request a
          service", and printing it twice on one screen reads as a template that was
          never finished. This row exists only to give the reader a way out. */}
      <section className="new-request-toolbar">
        <button type="button" className="button button-secondary" onClick={onCancel}>
          <ArrowLeft size={17} /> {t("common.cancel")}
        </button>
      </section>

      {/* Shaped like the brief form that is coming: the hero for the chosen
          service, then its fields. */}
      {loading && showSkeleton && <HeroSkeleton label="Copying the original request" />}

      {failed && (
        <div className="reorder-notice is-failed" role="alert">
          <TriangleAlert size={18} />
          <div>
            <strong>{failed}</strong>
            <span>{t("form.nothingSubmitted")}</span>
          </div>
        </div>
      )}

      {prefill && !failed && (
        <div className="reorder-notice">
          <CopyPlus size={18} />
          <div>
            <strong>{templateId ? t("form.startedFromBrief") : t("form.copiedFromRequest")}</strong>
            {/* Both of these are surprises if they are not said out loud. The date is
                dropped because a past deadline is not a deadline, and the price is
                today's because a rate is frozen at the moment a request is raised. */}
            <span>{t("form.prefillNote")}</span>
          </div>
        </div>
      )}

      {/* Only on a blank start. Offering saved briefs above a form that is already
          filled from one invites the reader to overwrite what they just chose. */}
      {!loading && !prefill && !from && !templateId && <SavedBriefs />}

      {!loading && (
        <RequestForm
          services={services}
          month={month}
          initialServiceId={serviceId}
          prefill={prefill}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}
